/**
 * The one place that turns a rejected API call into the copy a component
 * shows.
 *
 * The axios interceptor (`plugins/Cruxtify/api/index.ts`) is the single place
 * that reads an error *response body* — by the time a `.catch()` runs, the
 * rejection is a bare `AxiosError` with no `.response` left to inspect, only
 * the fields the interceptor hung on it (`message`, `payload`,
 * `is_application_error`, `is_timeout_error`, `reference`, `debug`). This is
 * the single place that reads *those*, so every action bar says the same thing
 * about the same failure instead of forking its own wording.
 */
export interface ApiErrorCopy {
  /** The headline shown in the error alert. Always a non-empty string. */
  message: string;
  /** Per-field validation messages, empty unless the server sent any. */
  list: string[];
  /** The 5xx envelope's `error_reference`, for the user to quote to support. */
  reference: string;
  /** The server's `debug` block — present only when the server chose to send
   * it, so the component performs no role check of its own. */
  detail: any;
}

export const GENERIC_API_ERROR_MESSAGE =
  "Something went wrong on our end. Please try again later. If the problem persists, contact support.";

export const TIMEOUT_API_ERROR_MESSAGE =
  "The request took too long to process. Your data may not have been saved. Please check and try again.";

/**
 * The HTTP status the interceptor preserved on a rejection — `withResponse()`
 * sets both `status` and `response`, so either is authoritative — or `null`
 * for a network / application error that never had one.
 */
export function apiErrorStatus(error: any): number | null {
  return error?.status ?? error?.response?.status ?? null;
}

/**
 * Whether a rejection is the API saying "this emailed one-click link is no
 * longer good": 401 from `Notification::findByTokenAndNotificationType`
 * ("Invalid or expired link") and 410 from its `abortIfCaseIsClosed()`. Both
 * are ordinary outcomes of an old email rather than faults, so a one-click
 * page answers them with `EmailResponseCard`'s invalid-link card instead of
 * reporting them as errors.
 */
export function isExpiredLinkError(error: any): boolean {
  const status = apiErrorStatus(error);

  return status === 401 || status === 410;
}

/**
 * @param error The value a `.catch()` received from an `$api` / model call.
 * @param labelForProperty Maps a validation-error key to the field label the
 *   user actually sees. Defaults to the raw key for callers with no model.
 */
export function apiErrorCopy(
  error: any,
  labelForProperty: (name: string) => string = (name) => name
): ApiErrorCopy {
  const copy: ApiErrorCopy = {
    message: GENERIC_API_ERROR_MESSAGE,
    list: [],
    reference: error?.reference || "",
    detail: error?.debug || null,
  };

  if (
    !error?.payload ||
    error.is_application_error ||
    error.message == "Server Error"
  ) {
    return copy;
  }

  if (error.is_timeout_error) {
    copy.message = TIMEOUT_API_ERROR_MESSAGE;
    return copy;
  }

  const validationErrors = error.payload.validation_errors;

  if (validationErrors) {
    copy.message = "There is a problem with the data you entered:";
    copy.list = Object.keys(validationErrors).map(
      (key) => `${labelForProperty(key)} - ${validationErrors[key].join(", ")}`
    );
    return copy;
  }

  copy.message = error.message;

  return copy;
}
